import { Injectable } from "@angular/core";
import { BeneficiaryKey } from "./../../models/domains/pbm/Benefit/beneficiary-key";
import { BeneficaryTemporaryLimit, Beneficiary } from "src/app/ui/models/domains/pbm/Beneficiary/beneficiary";
import { PagingRequest } from "./../../models/domains/paginator/paging-request";
import { ImportBeneficiaryResult } from "../../models/domains/pbm/Beneficiary/import-beneficiary-result";
import { BeneficiaryRepository } from "../../repositories/pbm/beneficiary.repository";
import { FileBeneficiary } from "../../models/domains/pbm/Beneficiary/file-beneficiary";
import { PageableResult } from "../../models/domains/paginator/pageable-result";
import { Dependent } from "../../models/domains/pbm/Beneficiary/dependent";
import { ExportOrderKey } from "../../models/domains/pbm/exports/export-order-key";
import { CardBlockDTO } from "../../models/domains/pbm/Beneficiary/dto/card-block-dto";
import { BlockCardReason } from "../../models/domains/pbm/Beneficiary/block-card-reason";
import { CardUnblockDTO } from "../../models/domains/pbm/Beneficiary/dto/card-unblock-dto";
import { OrderCardDTO } from "../../models/domains/pbm/Beneficiary/dto/order-card-dto";
import { FileExportService } from "../file-export.service";
import { IFileFileExportResult } from "../../models/domains/pbm/exports/file-export-result";
import { FiscalNumber } from "../../models/domains/pbm/fiscal-number";

@Injectable({ providedIn: "root" })
export class BeneficiaryService {
  private beneficiary: Beneficiary;
  private dependent: Dependent;
  private orderKey: ExportOrderKey;
  constructor(private beneficiaryRepository: BeneficiaryRepository, private fileExportService: FileExportService) {}

  public setBeneficiary(value: Beneficiary): void {
    this.beneficiary = value;
  }

  public getBeneficiary(): Beneficiary {
    return this.beneficiary;
  }

  public setDependent(value: Dependent): void {
    this.dependent = value;
  }

  public getDependent(): Dependent {
    return this.dependent;
  }

  public async importBeneficiary(fileBeneficiary: FileBeneficiary, fiscalNumber: FiscalNumber): Promise<string> {
    if (fileBeneficiary.hasFile()) {
      return await this.beneficiaryRepository.importBeneficiary(fileBeneficiary, fiscalNumber);
    }
  }

  public async importBlockCardNumber(fileBeneficiary: FileBeneficiary): Promise<ImportBeneficiaryResult> {
    return await this.beneficiaryRepository.importBlockCardNumber(fileBeneficiary);
  }

  public async importUnblockCardNumber(fileBeneficiary: FileBeneficiary): Promise<ImportBeneficiaryResult> {
    return await this.beneficiaryRepository.importUnblockCardNumber(fileBeneficiary);
  }

  public async findBeneficiariesByFiscalNumber(fiscalNumber: string, pagingRequest: PagingRequest, isDependent: boolean = null): Promise<PageableResult> {
    return await this.beneficiaryRepository.findBeneficiariesByFiscalNumber(fiscalNumber, pagingRequest, isDependent);
  }

  public async findBeneficiaryByKey(beneficiaryKey: BeneficiaryKey): Promise<Beneficiary> {
    return await this.beneficiaryRepository.findBeneficiaryByKey(beneficiaryKey);
  }

  public async saveBeneficiary(fiscalNumber: string, beneficiary: Beneficiary): Promise<any> {
    return await this.beneficiaryRepository.saveBeneficiary(fiscalNumber, beneficiary);
  }

  public async findBeneficiaryHolderBySocialNumber(socialNumber: string, fiscalNumber: string): Promise<Beneficiary> {
    return await this.beneficiaryRepository.findBeneficiaryHolderBySocialNumber(socialNumber, fiscalNumber);
  }

  public async findBeneficiaryBySocialNumber(socialNumber: string): Promise<Beneficiary | Dependent> {
    return await this.beneficiaryRepository.findBeneficiaryBySocialNumber(socialNumber);
  }

  public async findBeneficiaryExportOrderKey(fiscalNumber: string): Promise<ExportOrderKey> {
    return await this.beneficiaryRepository.findBeneficiaryExportOrderKey(fiscalNumber);
  }

  public async cancelBeneficiaryCard(beneficiaryKey: BeneficiaryKey): Promise<any> {
    return await this.beneficiaryRepository.cancelBeneficiaryCard(beneficiaryKey);
  }

  public async orderBeneficiaryCard(beneficiaryKey: BeneficiaryKey): Promise<OrderCardDTO> {
    return await this.beneficiaryRepository.orderBeneficiaryCard(beneficiaryKey);
  }

  public async blockBeneficiaryCard(cardBlockDTO: CardBlockDTO): Promise<any> {
    return await this.beneficiaryRepository.blockBeneficiaryCard(cardBlockDTO);
  }

  public async unblockBeneficiaryCard(cardUnblockDTO: CardUnblockDTO): Promise<any> {
    return await this.beneficiaryRepository.unblockBeneficiaryCard(cardUnblockDTO);
  }

  public async getBlockReasonList(): Promise<BlockCardReason[]> {
    return await this.beneficiaryRepository.getBlockReasonList();
  }

  public async exportBeneficiaryUsageFileByCardNumber(cardNumber: string): Promise<any> {
    return await this.beneficiaryRepository.exportBeneficiaryUsageFileByCardNumber(cardNumber);
  }

  async downloadFile() {
    const fileExportResult: IFileFileExportResult = await this.fileExportService.exportFileAsCSV(this.orderKey);
    if (!!fileExportResult) {
      fileExportResult.notifyDownload("importacao-beneficiarios");
    }
  }

  public async beneficiaryTemporaryLimit(temporaryLimit: BeneficaryTemporaryLimit): Promise<any> {
    return await this.beneficiaryRepository.beneficaryTemporaryLimit(temporaryLimit);
  }
}
